import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { apiClient, API, useAuth, usePermissions } from "../App";
import { toast } from "sonner";
import { 
  Edit2, 
  Trash2, 
  Plus, 
  Calendar,
  DollarSign,
  Users,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  ExternalLink,
  ListOrdered,
  Link2,
  FolderOpen,
  MessageSquare,
  Layout,
  Figma,
  HardDrive,
  Globe,
  X,
  PiggyBank,
  Upload,
  Eye,
  RefreshCw,
  FileImage,
  Loader2,
  FileBox,
  Copy,
  Download,
  Lock,
  Video,
  MoreHorizontal,
  Mic,
  Package,
  Boxes
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CurrencyInput } from "../components/ui/currency-input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import ProjectChangeOrdersPanel from "../components/change-orders/ProjectChangeOrdersPanel";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { usePmTestMode } from "../hooks/usePmTestMode";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Slider } from "../components/ui/slider";
import { ProgramAssignments } from "../components/program/ProgramAssignments";
import { ProjectScopeOverview } from "../components/program/ProjectScopeOverview";
import { PlanningCallStatusPanel } from "../components/activation/PlanningCallAutomation";
import { DeliverableMilestones } from "../components/program/DeliverableMilestones";
import ProgramCostingTab from "../components/costing/ProgramCostingTab";
import CommunicationsTab from "../components/program/CommunicationsTab";
import MilestonePaymentsTab from "../components/program/MilestonePaymentsTab";
import { ProgramCallsTab } from "../components/fathom/ProgramCallsTab";
import VexosTrackerTab from "../components/program/VexosTrackerTab";
import CloseAndDeliverDialog from "../components/program/CloseAndDeliverDialog";
import { ProgramHero } from "../components/program/ProgramHero";
import { ProgramSection, sectionActionClass } from "../components/program/ProgramSection";
import DeliveryManager from "./DeliveryManager";
import PlanningPopup from "../components/program/ScopeTimelineTab";


// Tabs this page can render. "planner", "estimator" and "scope-timeline" were
// retired/superseded — they are deliberately absent so old ?tab= links fall
// back to Overview. Planning (scope + both timelines + Parse Agreement) is a
// popup now, triggered from the hero row, not a tab — see PlanningPopup.
const PROGRAM_TABS = [
  "overview",
  "intake",
  "costing",
  "payments",
  "communications",
  "calls",
  "vexos",
  "delivery",
];

// Unified program stages - synced with Management View
const PROGRAM_STAGES = ["Queued", "Kickoff", "Production", "On Hold", "Delivered", "Closed"];
const RISK_LEVELS = ["low", "medium", "high"];
const DEPENDENCY_TYPES = ["Client Input", "Approval", "Internal Capacity", "External Vendor"];

// Stage colors for visual consistency
const STAGE_COLORS = {
  "Intake": "bg-slate-100 text-slate-700",
  "Kickoff": "bg-sky-100 text-sky-700",
  "Production": "bg-amber-100 text-amber-700",
  "Revisions": "bg-violet-100 text-violet-700",
  "QA/Final": "bg-emerald-100 text-emerald-700",
  "Delivered": "bg-green-100 text-green-700"
};

// Link type icons and colors
const LINK_TYPES = {
  slack: { icon: MessageSquare, label: "Slack Channel", color: "bg-[#4A154B] text-white" },
  dropbox: { icon: FolderOpen, label: "Dropbox Folder", color: "bg-[#3D6355] text-white" },
  miro: { icon: Layout, label: "Miro Board", color: "bg-[#DCD6C9] text-black" },
  figma: { icon: Figma, label: "Figma File", color: "bg-[#F24E1E] text-white" },
  drive: { icon: HardDrive, label: "Google Drive", color: "bg-[#4285F4] text-white" },
  notion: { icon: FileText, label: "Notion Page", color: "bg-[#000000] text-white" },
  custom: { icon: Globe, label: "Custom Link", color: "bg-gray-600 text-white" }
};

// Intake Data Tab Component - Apple-like minimal design
function IntakeDataTab({ intakeData, loading, isAdmin, programId, onRefresh }) {
  const [copiedLink, setCopiedLink] = useState(null);
  // State for adding new links
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkType, setNewLinkType] = useState("custom");
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(label);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedLink(null), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };
  
  const handleDownloadAgreement = async () => {
    try {
      const response = await apiClient.get(`/programs/${programId}/agreement/download`, {
        responseType: 'blob',
        // Same URL after a replace — bust any copy the browser cached of the old PDF.
        params: { _: Date.now() },
      });
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        toast.error("Agreement file not found or is empty. Please contact support.");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = intakeData?.agreement?.file_name || 'agreement.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Agreement downloaded");
    } catch (error) {
      console.error("Agreement download error:", error);
      const status = error?.response?.status;
      if (status === 404) {
        toast.error("Agreement file not found. The file may have been moved or deleted.");
      } else if (status === 403) {
        toast.error("You don't have permission to download this agreement.");
      } else if (status === 500) {
        toast.error("Server error while downloading agreement. Please try again later.");
      } else {
        toast.error("Failed to download agreement. Please check your connection and try again.");
      }
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!intakeData) {
    return (
      <Card className="border border-border rounded-2xl shadow-none">
        <CardContent className="py-12 text-center">
          <FileBox className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
          <p className="text-muted-foreground">No intake data available for this program</p>
        </CardContent>
      </Card>
    );
  }
  
  const { operational_links, scope, external_contacts, accounts_payable, financials, agreement, can_view_financials, can_view_agreement } = intakeData;
  const hasFathomLinks = operational_links?.fathom_links?.length > 0;
  const hasProjectFileLinks = operational_links?.project_file_links?.length > 0;
  const hasReferenceLinks = operational_links?.reference_links?.length > 0;
  const hasVideoLink = operational_links?.project_breakdown_video_link;
  const hasCustomLinks = operational_links?.custom_links?.length > 0;

  const handleAddLink = async () => {
    if (!newLinkUrl.trim()) {
      toast.error("URL is required");
      return;
    }
    setSavingLink(true);
    try {
      await apiClient.post(`/programs/${programId}/operational-links`, {
        type: newLinkType,
        title: newLinkTitle || undefined,
        url: newLinkUrl
      });
      toast.success("Link added");
      setNewLinkTitle("");
      setNewLinkUrl("");
      setShowAddLink(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to add link");
    } finally {
      setSavingLink(false);
    }
  };

  const handleDeleteLink = async (linkType, index) => {
    try {
      await apiClient.delete(`/programs/${programId}/operational-links`, {
        data: { type: linkType, index }
      });
      toast.success("Link removed");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to remove link");
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Operational Links Section */}
      <Card className="border border-border rounded-2xl shadow-none">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Operational Links
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowAddLink(!showAddLink)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Link
            </Button>
          </div>
          
          {/* Add Link Form */}
          {showAddLink && (
            <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Link Type</Label>
                  <Select value={newLinkType} onValueChange={setNewLinkType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom Link</SelectItem>
                      <SelectItem value="fathom">Fathom Recording</SelectItem>
                      <SelectItem value="project_file">Project File</SelectItem>
                      <SelectItem value="reference">Reference Link</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Title (optional)</Label>
                  <Input
                    value={newLinkTitle}
                    onChange={(e) => setNewLinkTitle(e.target.value)}
                    placeholder="e.g., Design Files"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">URL *</Label>
                <Input
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddLink} disabled={savingLink}>
                  {savingLink ? "Adding..." : "Add Link"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddLink(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Fathom Links */}
          {hasFathomLinks && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Fathom Recordings</p>
              <div className="space-y-2">
                {operational_links.fathom_links.map((link, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg group hover:bg-secondary/50 transition-colors"
                    data-testid={`fathom-link-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-md bg-purple-100 flex items-center justify-center shrink-0">
                        <Video className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">Fathom Recording #{idx + 1}</p>
                        <p className="text-xs text-muted-foreground truncate">{link.url || link}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(link.url || link, `fathom-${idx}`)}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className={`w-4 h-4 ${copiedLink === `fathom-${idx}` ? "text-green-600" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                      >
                        <a href={link.url || link} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Project File Links */}
          {hasProjectFileLinks && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Project Files</p>
              <div className="space-y-2">
                {operational_links.project_file_links.map((link, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg group hover:bg-secondary/50 transition-colors"
                    data-testid={`project-file-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
                        <FolderOpen className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{link.title || "Project File"}</p>
                        <p className="text-xs text-muted-foreground truncate">{link.url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(link.url, `project-${idx}`)}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className={`w-4 h-4 ${copiedLink === `project-${idx}` ? "text-green-600" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                      >
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Reference Links */}
          {hasReferenceLinks && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Reference Links</p>
              <div className="space-y-2">
                {operational_links.reference_links.map((link, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg group hover:bg-secondary/50 transition-colors"
                    data-testid={`reference-link-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                        <Globe className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">Reference #{idx + 1}</p>
                        <p className="text-xs text-muted-foreground truncate">{link.url || link}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(link.url || link, `ref-${idx}`)}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className={`w-4 h-4 ${copiedLink === `ref-${idx}` ? "text-green-600" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                      >
                        <a href={link.url || link} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Video Link */}
          {hasVideoLink && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Project Breakdown Video</p>
              <div 
                className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg group hover:bg-secondary/50 transition-colors"
                data-testid="video-link"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-8 h-8 rounded-md bg-red-100 flex items-center justify-center shrink-0">
                    <Video className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Breakdown Video</p>
                    <p className="text-xs text-muted-foreground truncate">{operational_links.project_breakdown_video_link}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(operational_links.project_breakdown_video_link, "video")}
                    className="h-8 w-8 p-0"
                  >
                    <Copy className={`w-4 h-4 ${copiedLink === "video" ? "text-green-600" : ""}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    asChild
                    className="h-8 w-8 p-0"
                  >
                    <a href={operational_links.project_breakdown_video_link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {!hasFathomLinks && !hasProjectFileLinks && !hasReferenceLinks && !hasVideoLink && !hasCustomLinks && (
            <p className="text-sm text-muted-foreground text-center py-4">No operational links. Click "Add Link" to add one.</p>
          )}
          
          {/* Custom Links */}
          {hasCustomLinks && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Custom Links</p>
              <div className="space-y-2">
                {operational_links.custom_links.map((link, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg group hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
                        <Link2 className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{link.title || `Link #${idx + 1}`}</p>
                        <p className="text-xs text-muted-foreground truncate">{link.url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(link.url, `custom-${idx}`)}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className={`w-4 h-4 ${copiedLink === `custom-${idx}` ? "text-green-600" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                      >
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteLink("custom", idx)}
                        className="h-8 w-8 p-0 text-red-500 opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Accounts Payable — its own card rather than a row in the list below,
          because it is not just another contact: every payment notice routes
          here, and a PM chasing a late invoice needs to know who is being
          emailed. Operational, so no financial gate. */}
      {accounts_payable?.email && (
        <Card className="border border-border rounded-2xl shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-600" />
              Accounts Payable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-3 bg-secondary/30 rounded-lg" data-testid="intake-accounts-payable">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium truncate">{accounts_payable.name}</p>
                {accounts_payable.title && (
                  <Badge variant="secondary" className="text-[10px]">
                    {accounts_payable.title}
                  </Badge>
                )}
              </div>
              <a
                href={`mailto:${accounts_payable.email}`}
                className="text-xs text-muted-foreground hover:text-primary truncate block mt-0.5"
              >
                {accounts_payable.email}
              </a>
              {accounts_payable.phone && (
                <p className="text-xs text-muted-foreground mt-0.5">{accounts_payable.phone}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Every payment notice for this project goes here, with the client contact copied.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Contacts — captured at intake, alongside the client's own
          details in the page header. Read-only here; sales owns the list. */}
      {external_contacts?.length > 0 && (
        <Card className="border border-border rounded-2xl shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-600" />
              Additional Contacts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="intake-external-contacts">
              {external_contacts.map((contact, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-3 p-3 bg-secondary/30 rounded-lg group"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{contact.name}</p>
                      {contact.role && (
                        <Badge variant="secondary" className="text-[10px]">{contact.role}</Badge>
                      )}
                    </div>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-xs text-muted-foreground hover:text-primary truncate block"
                    >
                      {contact.email}
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(contact.email, `contact-${idx}`)}
                    className="h-8 w-8 p-0 flex-shrink-0"
                  >
                    <Copy className={`w-4 h-4 ${copiedLink === `contact-${idx}` ? "text-green-600" : ""}`} />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scope Details Section */}
      {(scope?.scope_details || scope?.additional_services?.length > 0) && (
        <Card className="border border-border rounded-2xl shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Scope Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {scope.scope_details && (
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{scope.scope_details}</p>
              </div>
            )}
            
            {scope.additional_services?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Additional Services</p>
                <div className="flex flex-wrap gap-2">
                  {scope.additional_services.map((service, idx) => (
                    <Badge key={idx} variant="secondary">{service}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {scope.vertical_safe_zone_required && (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Vertical Safe Zone Required</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Documents Section - Admin Only */}
      {isAdmin && can_view_agreement && agreement?.has_agreement && (
        <Card className="border border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Legal Documents
              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-200">Admin Only</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-amber-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium">Signed Service Agreement</p>
                  <p className="text-xs text-muted-foreground">
                    {agreement.type === "link" ? "External Link" : (agreement.file_name || "Uploaded Document")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {agreement.type === "link" && agreement.link ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(agreement.link, "agreement")}
                      className="gap-2"
                    >
                      <Copy className={`w-4 h-4 ${copiedLink === "agreement" ? "text-green-600" : ""}`} />
                      Copy
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      asChild
                      className="gap-2"
                    >
                      <a href={agreement.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4" />
                        View
                      </a>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadAgreement}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      asChild
                      className="gap-2"
                    >
                      <a href={`${API}/programs/${programId}/agreement/download`} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4" />
                        View
                      </a>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Submission Info */}
      <div className="text-center py-4">
        <p className="text-xs text-muted-foreground">
          Submitted by {intakeData.submitted_by_name || "Unknown"} 
          {intakeData.submitted_at && ` on ${new Date(intakeData.submitted_at).toLocaleDateString()}`}
        </p>
      </div>
    </div>
  );
}

const ProgramDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Tab management — anything not in PROGRAM_TABS (e.g. a bookmark pointing at
  // the retired "planner"/"estimator" tabs) resolves to Overview, so the
  // content and the selected tab indicator never disagree.
  const requestedTab = searchParams.get("tab");
  const activeTab = PROGRAM_TABS.includes(requestedTab) ? requestedTab : "overview";
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };
  
  // PM Test Mode
  const pmTestMode = usePmTestMode();
  
  // Use backend permissions for financial visibility
  const { canViewFinancials: permCanViewFinancials, canViewCosting: permCanViewCosting } = usePermissions();
  
  // Check if user can view costing (backend-controlled with PM test mode override)
  const userRole = user?.role || "";
  const isActualPM = userRole === "Program Manager" || userRole === "Marketing PM";
  const isPM = isActualPM || pmTestMode?.isActive;
  const canViewCosting = permCanViewCosting && !pmTestMode?.isActive;
  const canViewFinancials = permCanViewFinancials && !pmTestMode?.isActive;
  // Intake Data tab is visible to Admin and PM roles
  const canViewIntakeData = ["COO", "Admin", "Program Manager", "Marketing PM"].includes(userRole);
  const isAdmin = ["COO", "Admin"].includes(userRole);
  // Milestone Payments tab - Finance sees full view, PMs see limited view
  const canViewPayments = (permCanViewFinancials && ["COO", "Admin", "Accountant"].includes(userRole) && !pmTestMode?.isActive) || isActualPM;
  
  const [program, setProgram] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [programLinks, setProgramLinks] = useState([]);
  const [hasAgreement, setHasAgreement] = useState(false);
  const [intakeData, setIntakeData] = useState(null);
  const [intakeDataLoading, setIntakeDataLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // UI States
  const [showEditProgram, setShowEditProgram] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showAddDependency, setShowAddDependency] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [showAgreementOverride, setShowAgreementOverride] = useState(false);
  const [agreementFile, setAgreementFile] = useState(null);
  const [agreementUploading, setAgreementUploading] = useState(false);
  const [timelineWeeks, setTimelineWeeks] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [vexosMeta, setVexosMeta] = useState(null);
  const [showCloseDeliver, setShowCloseDeliver] = useState(false);

  // Scope of Work state
  const [scopeScreenshot, setScopeScreenshot] = useState(null);
  const [scopeScreenshotUrl, setScopeScreenshotUrl] = useState(null);
  const [scopeUploading, setScopeUploading] = useState(false);
  const [showScopeModal, setShowScopeModal] = useState(false);

  // Marketing pipeline state — populated only for programs with marketing scope.
  // Drives the "Marketing Kickoff Required" badge + the "Mark Kickoff Complete" CTA.
  const [marketingModule, setMarketingModule] = useState(null);
  const isMarketingPM = userRole === "Marketing PM" || user?.id === marketingModule?.assigned_pm_user_id;
  const canManageMarketing = isAdmin || isMarketingPM;
  
  // Form states
  const [editData, setEditData] = useState({});
  
  // Edit deliverable state
  const [newMilestone, setNewMilestone] = useState({
    name: "",
    date_window: "",
    approval_required: false
  });
  const [newDependency, setNewDependency] = useState({
    dependency_type: "",
    blocking_party: "",
    impact_radius: ""
  });
  const [newLink, setNewLink] = useState({
    link_type: "slack",
    label: "",
    url: ""
  });

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    try {
      // Perf #4 Stage 2 — one aggregate read replaces the 6 parallel calls.
      // GET /programs/{id}/detail returns the same shapes field-for-field
      // (it delegates to the same handlers), so the state below is identical.
      const { data: detail } = await apiClient.get(`/programs/${id}/detail`);
      const programData = detail.program;

      setProgram(programData);
      setMilestones(detail.milestones);
      setDependencies(detail.dependencies);
      setActivityLogs(detail.activity_logs);
      setProgramLinks(detail.links);
      setEditData(programData);

      // Check for scope screenshot - fetch as blob since endpoint requires auth
      if (programData.scope_screenshot_filename) {
        try {
          const screenshotRes = await apiClient.get(`/programs/${id}/scope-screenshot/view`, {
            responseType: 'blob',
            validateStatus: (status) => status === 200 // Only treat 200 as success
          });
          if (screenshotRes.status === 200 && screenshotRes.data.size > 0) {
            const blobUrl = URL.createObjectURL(screenshotRes.data);
            setScopeScreenshotUrl(blobUrl);
          } else {
            setScopeScreenshotUrl(null);
          }
        } catch (screenshotErr) {
          // 404 is expected if screenshot was deleted/corrupted
          setScopeScreenshotUrl(null);
        }
      }
      
      // Check for agreement in program_documents
      try {
        const agreementRes = await apiClient.get(`/programs/${id}/agreement`);
        // Treat any active agreement record as "has agreement" — including link-type
        setHasAgreement(Boolean(agreementRes.data));
      } catch {
        // Check legacy fields on program
        setHasAgreement(Boolean(programData.agreement_file_id || programData.signed_agreement_file_id));
      }

      // Marketing module — best-effort fetch. Silent on 404/error since most
      // programs won't have marketing scope and the badge simply won't render.
      try {
        const mktgRes = await apiClient.get(`/programs/${id}/marketing`);
        setMarketingModule(mktgRes.data?.exists ? mktgRes.data : null);
      } catch {
        setMarketingModule(null);
      }

      // VEXOS tracker meta — decides whether the VEXOS tab shows. Best-effort.
      try {
        const vexosRes = await apiClient.get(`/programs/${id}/vexos-tracker`);
        setVexosMeta(vexosRes.data || null);
      } catch {
        setVexosMeta(null);
      }
    } catch (error) {
      toast.error("Failed to load program");
      navigate("/programs");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch intake data when switching to intake tab
  const fetchIntakeData = async (forceRefresh = false) => {
    if (!canViewIntakeData || (!forceRefresh && intakeData)) return;
    setIntakeDataLoading(true);
    try {
      const response = await apiClient.get(`/programs/${id}/intake-data`);
      setIntakeData(response.data);
    } catch (error) {
      console.error("Failed to load intake data:", error);
      // Don't show error toast - just means no intake data available
    } finally {
      setIntakeDataLoading(false);
    }
  };
  
  // Refresh intake data (bypasses cache check)
  const refreshIntakeData = () => fetchIntakeData(true);
  
  // Load intake data when tab switches to "intake"
  useEffect(() => {
    if (activeTab === "intake" && canViewIntakeData && !intakeData) {
      fetchIntakeData();
    }
  }, [activeTab]);

  const handleAddLink = async () => {
    if (!newLink.label.trim() || !newLink.url.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      const response = await apiClient.post(`/programs/${id}/links`, newLink);
      setProgramLinks(prev => [...prev, response.data]);
      setShowAddLink(false);
      setNewLink({ link_type: "slack", label: "", url: "" });
      toast.success("Link added");
    } catch (error) {
      toast.error("Failed to add link");
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await apiClient.delete(`/programs/${id}/links/${linkId}`);
      setProgramLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success("Link removed");
    } catch (error) {
      toast.error("Failed to remove link");
    }
  };

  const handleUpdateProgram = async () => {
    try {
      // Closing a project must go through the Close & Deliver form (Dropbox
      // link + team notification + portfolio publish) — never a bare stage
      // change. Funnel a Closed selection into that flow instead.
      if (editData.stage === "Closed" && program.stage !== "Closed") {
        setShowEditProgram(false);
        setShowCloseDeliver(true);
        return;
      }
      // Ensure numeric values are valid numbers
      const payload = {
        program_name: editData.program_name,
        stage: editData.stage,
        contract_value: parseFloat(editData.contract_value) || 0,
        cash_collected: parseFloat(editData.cash_collected) || 0,
        start_window: editData.start_window || null,
        end_window: editData.end_window || null
      };

      await apiClient.put(`/programs/${id}`, payload);
      setShowEditProgram(false);
      fetchAllData();
      toast.success("Program updated");
    } catch (error) {
      console.error("Program update error:", error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else {
        toast.error("Failed to update program. Check console for details.");
      }
    }
  };

  const handleDeleteProgram = async () => {
    try {
      await apiClient.delete(`/programs/${id}`);
      toast.success("Program deleted");
      navigate("/programs");
    } catch (error) {
      toast.error("Failed to delete program");
    }
  };



  // Edit deliverable

  // Delete deliverable

  const handleAddMilestone = async () => {
    if (!newMilestone.name.trim() || !newMilestone.date_window) {
      toast.error("Name and date are required");
      return;
    }
    try {
      await apiClient.post("/milestones", {
        program_id: id,
        ...newMilestone
      });
      setShowAddMilestone(false);
      setNewMilestone({ name: "", date_window: "", approval_required: false });
      fetchAllData();
      toast.success("Milestone added");
    } catch (error) {
      toast.error("Failed to add milestone");
    }
  };

  const handleToggleMilestone = async (milestoneId, completed) => {
    try {
      await apiClient.put(`/milestones/${milestoneId}`, { completed: !completed });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const handleAddDependency = async () => {
    if (!newDependency.dependency_type || !newDependency.blocking_party.trim()) {
      toast.error("Type and blocking party are required");
      return;
    }
    try {
      await apiClient.post("/dependencies", {
        linked_entity_type: "program",
        linked_entity_id: id,
        ...newDependency
      });
      setShowAddDependency(false);
      setNewDependency({ dependency_type: "", blocking_party: "", impact_radius: "" });
      fetchAllData();
      toast.success("Dependency added");
    } catch (error) {
      toast.error("Failed to add dependency");
    }
  };

  const handleResolveDependency = async (depId) => {
    try {
      await apiClient.put(`/dependencies/${depId}`, { resolved: true });
      fetchAllData();
      toast.success("Dependency resolved");
    } catch (error) {
      toast.error("Failed to resolve dependency");
    }
  };

  const handleViewAgreement = async () => {
    // Download and open agreement PDF in new tab
    try {
      const response = await apiClient.get(`/programs/${id}/agreement/download`, {
        responseType: 'blob',
        // Same URL after a replace — bust any copy the browser cached of the old PDF.
        params: { _: Date.now() },
      });
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        toast.error("Agreement file not found or is empty. Please contact support.");
        return;
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error("Agreement view error:", error);
      const status = error?.response?.status;
      if (status === 404) {
        toast.error("Agreement file not found. The file may have been moved or deleted.");
      } else if (status === 403) {
        toast.error("You don't have permission to view this agreement.");
      } else if (status === 500) {
        toast.error("Server error while loading agreement. Please try again later.");
      } else {
        toast.error("Failed to load agreement PDF. Please check your connection and try again.");
      }
    }
  };

  const handleDownloadAgreement = async () => {
    // Download agreement PDF as file
    try {
      const agreementRes = await apiClient.get(`/programs/${id}/agreement`);
      const agreementData = agreementRes.data;
      
      const response = await apiClient.get(`/programs/${id}/agreement/download`, {
        responseType: 'blob',
        // Same URL after a replace — bust any copy the browser cached of the old PDF.
        params: { _: Date.now() },
      });
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        toast.error("Agreement file not found or is empty. Please contact support.");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = agreementData?.file_name || `${program?.program_name || 'agreement'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Agreement downloaded");
    } catch (error) {
      console.error("Agreement download error:", error);
      const status = error?.response?.status;
      if (status === 404) {
        toast.error("Agreement file not found. The file may have been moved or deleted.");
      } else if (status === 403) {
        toast.error("You don't have permission to download this agreement.");
      } else if (status === 500) {
        toast.error("Server error while downloading agreement. Please try again later.");
      } else {
        toast.error("Failed to download agreement. Please check your connection and try again.");
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getStageBadgeClass = (stage) => {
    return STAGE_COLORS[stage] || STAGE_COLORS["Intake"];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!program) {
    return null;
  }

  const collectionProgress = program.contract_value > 0 
    ? Math.round((program.cash_collected / program.contract_value) * 100) 
    : 0;

  const unresolvedDependencies = dependencies.filter(d => !d.resolved);

  return (
    <div className="max-w-4xl mx-auto" data-testid="program-detail">
      {/* Header */}
      <div className="mb-8">
        <ProgramHero
          program={program}
          programId={id}
          isAdmin={isAdmin}
          hasAgreement={hasAgreement}
          marketingModule={marketingModule}
          canManageMarketing={canManageMarketing}
          onMarketingUpdate={(patch) => setMarketingModule(prev => prev ? { ...prev, ...patch } : prev)}
          onRefresh={fetchAllData}
          onEdit={() => setShowEditProgram(true)}
          onDelete={() => setShowDeleteConfirm(true)}
          onCloseDeliver={() => setShowCloseDeliver(true)}
          onViewAgreement={handleViewAgreement}
          onDownloadAgreement={handleDownloadAgreement}
          onReplaceAgreement={() => setShowAgreementOverride(true)}
        />

        {/* Planning — scope, contract breakdown, Parse Agreement, both timelines */}
        <div className="mt-4 flex justify-end">
          <PlanningPopup programId={id} programName={program?.program_name} canViewFinancials={canViewFinancials} />
        </div>

        {/* What's in this project, and where each delivery stands */}
        <div className="mt-6">
          <ProjectScopeOverview programId={id} />
        </div>
        
        {/* Tab Navigation */}
        <div className="mt-6 border-b border-border">
          <div className="flex gap-0.5 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "overview"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
              }`}
              data-testid="tab-overview"
            >
              Overview
            </button>
            {canViewIntakeData && (
              <button
                onClick={() => setActiveTab("intake")}
                className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === "intake"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                }`}
                data-testid="tab-intake"
              >
                <FileBox className="w-3.5 h-3.5" />
                Sales Intake
              </button>
            )}
            {canViewCosting && (
              <button
                onClick={() => setActiveTab("costing")}
                className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === "costing"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                }`}
                data-testid="tab-costing"
              >
                <PiggyBank className="w-3.5 h-3.5" />
                Costing
              </button>
            )}
            {canViewPayments && (
              <button
                onClick={() => setActiveTab("payments")}
                className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === "payments"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                }`}
                data-testid="tab-payments"
              >
                <DollarSign className="w-3.5 h-3.5" />
                Payment Schedule
              </button>
            )}
            <button
              onClick={() => setActiveTab("calls")}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === "calls"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
              }`}
              data-testid="tab-calls"
            >
              <Mic className="w-3.5 h-3.5" />
              Calls
            </button>
            {vexosMeta?.is_vexos && (
              <button
                onClick={() => setActiveTab("vexos")}
                className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === "vexos"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                }`}
                data-testid="tab-vexos"
              >
                <Boxes className="w-3.5 h-3.5" />
                VEXOS
              </button>
            )}
            {canViewIntakeData && (
              <button
                onClick={() => setActiveTab("delivery")}
                className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  activeTab === "delivery"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                }`}
                data-testid="tab-delivery"
              >
                <Package className="w-3.5 h-3.5" />
                Delivery
              </button>
            )}
            {/* Communications sits last — deliberately the far-right tab. */}
            <button
              onClick={() => setActiveTab("communications")}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === "communications"
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
              }`}
              data-testid="tab-communications"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Communications
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "intake" && canViewIntakeData ? (
        <div className="mt-6">
          <IntakeDataTab 
            intakeData={intakeData} 
            loading={intakeDataLoading} 
            isAdmin={isAdmin}
            programId={id}
            onRefresh={refreshIntakeData}
          />
        </div>
      ) : activeTab === "costing" && canViewCosting ? (
        <div className="mt-6">
          <ProgramCostingTab programId={id} />
        </div>
      ) : activeTab === "payments" && canViewPayments ? (
        <div className="mt-6">
          <MilestonePaymentsTab programId={id} />
        </div>
      ) : activeTab === "communications" ? (
        <div className="mt-6">
          <CommunicationsTab programId={id} programName={program?.program_name} />
        </div>
      ) : activeTab === "calls" ? (
        <div className="mt-6">
          <ProgramCallsTab programId={id} />
        </div>
      ) : activeTab === "vexos" ? (
        <div className="mt-6">
          <VexosTrackerTab programId={id} />
        </div>
      ) : activeTab === "delivery" && canViewIntakeData ? (
        <div className="mt-6">
          <DeliveryManager programId={id} embedded />
        </div>
      ) : (
      /* Main Content - Overview Tab */
      <div className="space-y-6 md:space-y-8 mt-6">
        {/* Program Summary — same hairline surface as the cards above the tabs. */}
        <div className="rounded-2xl border border-border px-5 py-5 sm:px-6">
          {/* Financial Row - Hidden from PM */}
          {canViewFinancials && (
            <>
              <div className="grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-10">
                <div>
                  <p className="label-text mb-1.5">Contract Value</p>
                  <p className="text-[22px] md:text-2xl leading-none font-light text-foreground tabular-nums" style={{ fontFamily: 'var(--font-sans)' }}>
                    {formatCurrency(program.contract_value)}
                  </p>
                  {program.subscription_contract_value > 0 && (
                    <p className="text-xs text-muted-foreground mt-1.5 tabular-nums">
                      {formatCurrency(program.one_time_contract_value)} one-time + {formatCurrency(program.subscription_contract_value)} subscription
                    </p>
                  )}
                </div>
                <div>
                  <p className="label-text mb-1.5">Collected</p>
                  <p className="text-[22px] md:text-2xl leading-none font-light text-[#2E7D55] tabular-nums" style={{ fontFamily: 'var(--font-sans)' }}>
                    {formatCurrency(program.cash_collected)}
                  </p>
                  {program.subscription_contract_value > 0 && (
                    <p className="text-xs text-muted-foreground mt-1.5 tabular-nums">
                      incl. {formatCurrency(program.subscription_cash_collected || 0)} subscription
                    </p>
                  )}
                </div>
                <div>
                  <p className="label-text mb-1.5">Exposure</p>
                  <p className="text-[22px] md:text-2xl leading-none font-light text-[#B96A43] tabular-nums" style={{ fontFamily: 'var(--font-sans)' }}>
                    {formatCurrency(program.exposure_remaining)}
                  </p>
                </div>
              </div>
              <Separator className="my-6" />
            </>
          )}

          {/* Timeline Row - Always Visible */}
          <div className="grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-10">
            <div>
              <p className="label-text mb-1.5">Timeline</p>
              <p className="text-[13px] text-foreground">
                {program.start_window ? new Date(program.start_window).toLocaleDateString() : 'TBD'}
                {' → '}
                {program.end_window ? new Date(program.end_window).toLocaleDateString() : 'TBD'}
              </p>
            </div>
            <div>
              <p className="label-text mb-1.5">Submitted By</p>
              <p className="text-[13px] font-medium text-foreground">{program.salesperson_name || program.created_by_name || 'Unknown'}</p>
            </div>
            {canViewFinancials && (
              <div className="col-span-2 md:col-span-1">
                <p className="label-text mb-2">Collection</p>
                <div className="flex items-center gap-3">
                  <Progress value={collectionProgress} className="flex-1 h-1.5 bg-secondary" />
                  <span className="text-[13px] font-medium text-foreground tabular-nums">{collectionProgress}%</span>
                </div>
              </div>
            )}
            <PlanningCallStatusPanel programId={id} className="col-span-2 md:col-span-1" />
          </div>
        </div>

        {/* Quick Links / Resources */}
        <ProgramSection
          title="Quick Links"
          data-testid="program-links"
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddLink(true)}
              className={sectionActionClass}
              data-testid="add-link-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add
            </Button>
          }
        >
          {programLinks.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              No links yet — add Slack channels, Dropbox folders, Miro boards, and more.
            </p>
          ) : (
            /* Quiet rows, not saturated brand tiles: the brand colour lives in
               the small icon only, so a wall of links stays readable. */
            <div className="grid gap-2 sm:grid-cols-2">
              {programLinks.map((link) => {
                const linkConfig = LINK_TYPES[link.link_type] || LINK_TYPES.custom;
                const IconComponent = linkConfig.icon;

                return (
                  <div
                    key={link.id}
                    className="group relative flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 transition-colors hover:bg-secondary"
                    data-testid={`link-${link.id}`}
                  >
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${linkConfig.color}`}>
                      <IconComponent className="h-3.5 w-3.5" />
                    </span>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 flex-1"
                    >
                      <span className="block truncate text-[13px] font-medium text-foreground">
                        {link.label}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {linkConfig.label}
                      </span>
                    </a>
                    <button
                      onClick={(e) => { e.preventDefault(); handleDeleteLink(link.id); }}
                      className="shrink-0 rounded-full p-1 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-background hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                      title="Remove link"
                      data-testid={`delete-link-${link.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </ProgramSection>

        {/* Scope of Work Screenshot */}
        <ProgramSection
          title="Scope of Work"
          data-testid="scope-of-work"
          action={
            <div className="flex items-center gap-1">
              {hasAgreement && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleViewAgreement}
                  className={sectionActionClass}
                  data-testid="view-agreement-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" />
                  Agreement
                </Button>
              )}
              <label htmlFor="scope-upload" className="cursor-pointer">
                <Button
                  variant="ghost"
                  size="sm"
                  className={sectionActionClass}
                  asChild
                  disabled={scopeUploading}
                  data-testid="upload-scope-btn"
                >
                  <span>
                    {scopeUploading ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading…</>
                    ) : (
                      <><Upload className="w-3.5 h-3.5 mr-1" /> Upload</>
                    )}
                  </span>
                </Button>
              </label>
                <input
                  id="scope-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setScopeUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await apiClient.post(`/programs/${id}/scope-screenshot`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      // Fetch the uploaded image as blob
                      const screenshotRes = await apiClient.get(`/programs/${id}/scope-screenshot/view`, {
                        responseType: 'blob'
                      });
                      const blobUrl = URL.createObjectURL(screenshotRes.data);
                      setScopeScreenshotUrl(blobUrl);
                      toast.success("Scope screenshot uploaded");
                    } catch (error) {
                      const detail = error.response?.data?.detail || "Failed to upload screenshot";
                      toast.error(detail);
                      console.error("Upload error:", error.response?.data || error.message);
                    } finally {
                      setScopeUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
            </div>
          }
        >
          {scopeScreenshotUrl ? (
            <button
              type="button"
              className="block w-full text-left transition-opacity hover:opacity-90"
              onClick={() => setShowScopeModal(true)}
            >
              <img
                src={scopeScreenshotUrl}
                alt="Scope of Work"
                className="w-full max-h-[300px] object-contain rounded-xl border border-border"
              />
              <p className="mt-2 text-xs text-muted-foreground">Click to enlarge</p>
            </button>
          ) : (
            <p className="text-[13px] text-muted-foreground">
              {hasAgreement
                ? "No screenshot yet — upload one, or open the agreement above."
                : "No screenshot yet — upload one to show the scope here."}
            </p>
          )}
        </ProgramSection>

        {/* Team & Resources */}
        <ProgramAssignments programId={id} programName={program?.name} />

        {/* Dependencies / Blockers */}
        {unresolvedDependencies.length > 0 && (
          <ProgramSection
            title="Active Blockers"
            tone="alert"
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddDependency(true)}
                className={sectionActionClass}
                data-testid="add-blocker-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add
              </Button>
            }
            bodyClassName="mt-4 space-y-2"
          >
            {unresolvedDependencies.map((dep) => (
              <div
                key={dep.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5"
                data-testid={`blocker-${dep.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground truncate">{dep.blocking_party}</p>
                  <p className="text-xs text-muted-foreground">{dep.dependency_type}</p>
                  {dep.impact_radius && (
                    <p className="mt-1 text-xs text-[#B96A43]">{dep.impact_radius}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResolveDependency(dep.id)}
                  className="h-8 shrink-0 rounded-full bg-secondary px-3 text-[13px] font-medium text-foreground hover:bg-border/70"
                  data-testid={`resolve-blocker-${dep.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                  Resolve
                </Button>
              </div>
            ))}
          </ProgramSection>
        )}

        {/* Deliverable Milestones - Primary Progress Tracking */}
        <DeliverableMilestones programId={id} onProgressChange={fetchAllData} />

        {/* Phase 3 — Approved Additional Scope (Change Orders) */}
        {program?.id && (
          <ProjectChangeOrdersPanel
            projectId={program.id}
            projectName={program.program_name || program.project_name || program.name}
            canCreate={true}
          />
        )}

        {/* Upsell recommendations are NOT on this page by design — they live at
            /upsells/opportunities (sidebar → Sales → Upsell Opportunities). This
            page is for running the project. */}

        {/* The Commitments section is gone. Its `deliverables` rows came from a
            regex over the agreement PDF — quantities baked into strings
            ("3 x Exterior Rendering"), prose fragments, 96 of 104 rows stuck in
            Queued, none with acceptance criteria. Deliverable Milestones above is
            now the project's scope of record, read from the proposal. The
            collection itself still backs the upsell engine's foundation
            detection for projects with no linked quote. */}

        {/* Activity Log */}
        <Collapsible open={activityOpen} onOpenChange={setActivityOpen}>
          <Card className="border border-border rounded-2xl shadow-none">
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-3 cursor-pointer hover:bg-secondary/50 transition-colors rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <CardTitle className="label-text">Activity Log</CardTitle>
                  {activityOpen ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {activityLogs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
                ) : (
                  <div className="space-y-3">
                    {activityLogs.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                        <div>
                          <p>
                            <span className="font-medium">{log.user_name || 'System'}</span>
                            {' '}{log.action} {log.entity_type}
                            {log.details && <span className="text-muted-foreground"> - {log.details}</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(log.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
      )}

      {/* Edit Program Dialog */}
      <Dialog open={showEditProgram} onOpenChange={(open) => {
        setShowEditProgram(open);
        if (open && editData.timeline_weeks_max) {
          setTimelineWeeks(editData.timeline_weeks_max);
          // Auto-calculate end date if start exists but end doesn't
          if (editData.start_window && !editData.end_window) {
            const weeks = editData.timeline_weeks_max;
            const startDate = new Date(editData.start_window);
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + (weeks * 7));
            setEditData(prev => ({
              ...prev,
              end_window: endDate.toISOString().split('T')[0]
            }));
          }
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Edit Program</DialogTitle>
            <DialogDescription>Update program details, stage, and financials.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Program Name</Label>
              <Input
                value={editData.program_name || ""}
                onChange={(e) => setEditData({ ...editData, program_name: e.target.value })}
                data-testid="edit-program-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stage</Label>
                <Select
                  value={editData.stage}
                  onValueChange={(value) => setEditData({ ...editData, stage: value })}
                >
                  <SelectTrigger data-testid="edit-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAM_STAGES.map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Financial Fields - Hidden in PM Test Mode */}
            {!pmTestMode.isActive && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contract Value</Label>
                  <CurrencyInput
                    value={editData.contract_value || 0}
                    onChange={(value) => setEditData({ ...editData, contract_value: value })}
                    data-testid="edit-contract-value"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cash Collected</Label>
                  <CurrencyInput
                    value={editData.cash_collected || 0}
                    onChange={(value) => setEditData({ ...editData, cash_collected: value })}
                    data-testid="edit-cash-collected"
                  />
                </div>
              </div>
            )}

            {/* Timeline Slider - only show if timeline data exists */}
            {(editData.timeline_weeks_min && editData.timeline_weeks_max) && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between">
                  <Label className="text-blue-700">Contract Timeline</Label>
                  <span className="text-sm font-semibold text-blue-700">
                    {timelineWeeks || editData.timeline_weeks_max} weeks
                  </span>
                </div>
                <Slider
                  value={[timelineWeeks || editData.timeline_weeks_max]}
                  min={editData.timeline_weeks_min}
                  max={editData.timeline_weeks_max}
                  step={1}
                  onValueChange={(value) => {
                    const weeks = value[0];
                    setTimelineWeeks(weeks);
                    // Auto-calculate end date if start date is set
                    if (editData.start_window) {
                      const startDate = new Date(editData.start_window);
                      const endDate = new Date(startDate);
                      endDate.setDate(endDate.getDate() + (weeks * 7));
                      setEditData({ 
                        ...editData, 
                        end_window: endDate.toISOString().split('T')[0] 
                      });
                    }
                  }}
                  className="w-full"
                  data-testid="timeline-slider"
                />
                <div className="flex justify-between text-xs text-blue-600">
                  <span>{editData.timeline_weeks_min} weeks (min)</span>
                  <span>{editData.timeline_weeks_max} weeks (max)</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editData.start_window || ""}
                  onChange={(e) => {
                    const newStartDate = e.target.value;
                    let newEndDate = editData.end_window;
                    
                    // Auto-calculate end date using timeline if available
                    if (newStartDate && editData.timeline_weeks_max) {
                      const weeks = timelineWeeks || editData.timeline_weeks_max;
                      const startDate = new Date(newStartDate);
                      const endDate = new Date(startDate);
                      endDate.setDate(endDate.getDate() + (weeks * 7));
                      newEndDate = endDate.toISOString().split('T')[0];
                    }
                    
                    setEditData({ 
                      ...editData, 
                      start_window: newStartDate,
                      end_window: newEndDate
                    });
                  }}
                  data-testid="edit-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date {editData.timeline_weeks_max && <span className="text-xs text-blue-600">(auto-calculated)</span>}</Label>
                <Input
                  type="date"
                  value={editData.end_window || ""}
                  onChange={(e) => setEditData({ ...editData, end_window: e.target.value })}
                  data-testid="edit-end-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProgram(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleUpdateProgram} className="rounded-full" data-testid="save-program-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Add Milestone Dialog */}
      <Dialog open={showAddMilestone} onOpenChange={setShowAddMilestone}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Add Milestone</DialogTitle>
            <DialogDescription>Add a delivery checkpoint milestone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Milestone Name *</Label>
              <Input
                value={newMilestone.name}
                onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                placeholder="e.g., Creative Approval"
                data-testid="input-milestone-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newMilestone.date_window}
                onChange={(e) => setNewMilestone({ ...newMilestone, date_window: e.target.value })}
                data-testid="input-milestone-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="approval_required"
                checked={newMilestone.approval_required}
                onChange={(e) => setNewMilestone({ ...newMilestone, approval_required: e.target.checked })}
                className="rounded border-gray-300"
                data-testid="input-milestone-approval"
              />
              <Label htmlFor="approval_required" className="text-sm font-normal">Approval required</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMilestone(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleAddMilestone} className="rounded-full" data-testid="save-milestone-btn">
              Add Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dependency Dialog */}
      <Dialog open={showAddDependency} onOpenChange={setShowAddDependency}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Add Blocker</DialogTitle>
            <DialogDescription>Record what is blocking execution.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Dependency Type *</Label>
              <Select
                value={newDependency.dependency_type}
                onValueChange={(value) => setNewDependency({ ...newDependency, dependency_type: value })}
              >
                <SelectTrigger data-testid="select-dependency-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {DEPENDENCY_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Blocking Party *</Label>
              <Input
                value={newDependency.blocking_party}
                onChange={(e) => setNewDependency({ ...newDependency, blocking_party: e.target.value })}
                placeholder="e.g., Client - John Smith"
                data-testid="input-blocking-party"
              />
            </div>
            <div className="space-y-2">
              <Label>Impact Description</Label>
              <Textarea
                value={newDependency.impact_radius}
                onChange={(e) => setNewDependency({ ...newDependency, impact_radius: e.target.value })}
                placeholder="What's blocked by this?"
                data-testid="input-impact-radius"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDependency(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleAddDependency} className="rounded-full" data-testid="save-dependency-btn">
              Add Blocker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Add Quick Link</DialogTitle>
            <DialogDescription>Connect to Slack, Dropbox, Miro, or any other resource.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Link Type</Label>
              <Select
                value={newLink.link_type}
                onValueChange={(value) => setNewLink({ ...newLink, link_type: value })}
              >
                <SelectTrigger data-testid="select-link-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LINK_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={newLink.label}
                onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                placeholder="e.g., Project Slack Channel"
                data-testid="input-link-label"
              />
            </div>
            <div className="space-y-2">
              <Label>URL *</Label>
              <Input
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                placeholder="https://..."
                data-testid="input-link-url"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLink(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleAddLink} className="rounded-full" data-testid="save-link-btn">
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close & Deliver */}
      <CloseAndDeliverDialog
        programId={id}
        projectName={program.program_name}
        open={showCloseDeliver}
        onOpenChange={setShowCloseDeliver}
        onClosed={fetchAllData}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{program.program_name}&quot;? This will also delete all associated deliverables, milestones, and dependencies. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProgram}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Agreement Override Dialog */}
      <Dialog open={showAgreementOverride} onOpenChange={setShowAgreementOverride}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>
              {hasAgreement ? "Replace Agreement" : "Upload Agreement"}
            </DialogTitle>
            <DialogDescription>
              {hasAgreement 
                ? "Upload a new agreement to replace the existing one. The new agreement will be parsed and deliverables will be updated."
                : "Upload an agreement document. The system will parse it and create deliverables automatically."
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {hasAgreement && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Warning</p>
                    <p className="text-xs mt-1">
                      Replacing the agreement will re-parse deliverables. The previous agreement will be versioned but existing deliverable modifications may be affected.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Agreement Document (PDF)</Label>
              <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setAgreementFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="agreement-file-input"
                />
                <label htmlFor="agreement-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  {agreementFile ? (
                    <p className="text-sm font-medium">{agreementFile.name}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Click to select PDF file</p>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAgreementOverride(false); setAgreementFile(null); }} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                if (!agreementFile) {
                  toast.error("Please select a file");
                  return;
                }
                setAgreementUploading(true);
                try {
                  const formData = new FormData();
                  formData.append('file', agreementFile);
                  formData.append('replace_existing', hasAgreement ? 'true' : 'false');
                  
                  await apiClient.post(`/programs/${id}/agreement`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  
                  toast.success(hasAgreement ? "Agreement replaced and re-parsed" : "Agreement uploaded and parsed");
                  setShowAgreementOverride(false);
                  setAgreementFile(null);
                  fetchAllData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to upload agreement");
                } finally {
                  setAgreementUploading(false);
                }
              }}
              disabled={!agreementFile || agreementUploading}
              className="rounded-full"
              data-testid="upload-agreement-confirm-btn"
            >
              {agreementUploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                hasAgreement ? "Replace & Re-parse" : "Upload & Parse"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scope of Work Zoom Modal */}
      <Dialog open={showScopeModal} onOpenChange={setShowScopeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Scope of Work - {program?.program_name}</DialogTitle>
          </DialogHeader>
          {scopeScreenshotUrl && (
            <div className="flex items-center justify-center p-4">
              <img 
                src={scopeScreenshotUrl} 
                alt="Scope of Work - Full Size"
                className="max-w-full max-h-[70vh] object-contain rounded-lg border shadow-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramDetail;
